import { useLanguage } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Mail, MapPin, Loader2 } from "lucide-react";
import {
  GitHubLogoIcon,
  LinkedInLogoIcon,
  FileIcon,
} from "@radix-ui/react-icons";

export const ContactSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    if (document.querySelector('script[src*="recaptcha"]')) {
      setRecaptchaLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/api.js?render=6LdQ8vEqAAAAAE6dkwFdJH4Zs-4mBz03KAKTuded";
    script.async = true;
    script.defer = true;
    script.onload = () => setRecaptchaLoaded(true);
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const formSchema = z.object({
    name: z.string().min(2, { message: t("contact.nameerror") }),
    email: z.string().email({ message: t("contact.emailerror") }),
    message: z.string().min(10, { message: t("contact.messageerror") }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const executeRecaptcha = async () => {
    if (!window.grecaptcha || !recaptchaLoaded) {
      console.error("reCAPTCHA not loaded");
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(
        "6LdQ8vEqAAAAAE6dkwFdJH4Zs-4mBz03KAKTuded",
        { action: "submit" }
      );
      return token;
    } catch (error) {
      console.error("reCAPTCHA execution error:", error);
      return null;
    }
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    setStatusMessage("");

    try {
      const recaptchaToken = await executeRecaptcha();

      if (!recaptchaToken) {
        throw new Error(
          "Failed to verify you're not a robot. Please try again."
        );
      }

      const response = await fetch("/backend/api/send-email.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...values,
          recaptchaToken,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(t("contact.success"));
        form.reset();
        setStatusMessage(t("contact.success"));
      } else {
        toast.error(data.message || t("contact.error"));
        setStatusMessage(data.message || t("contact.error"));
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(t("contact.error"));
      setStatusMessage(t("contact.error"));
    } finally {
      setIsSubmitting(false);
    }
  }

  const socialLinks = [
    {
      icon: <GitHubLogoIcon className="h-5 w-5" />,
      url: "https://github.com/adityakandele",
      label: "GitHub",
    },
    {
      icon: <LinkedInLogoIcon className="h-5 w-5" />,
      url: "https://linkedin.com/in/adityakandele",
      label: "LinkedIn",
    },
  ];

  const contactInfo = [
    { icon: <Mail className="h-5 w-5" />, text: "kandele.aditya@gmail.com" },
    {
      icon: <MapPin className="h-5 w-5" />,
      text: t("contact.currentlociation") + " : Barcelona, Spain",
    },
  ];

  return (
    <section id="contact" ref={sectionRef} className="section-padding relative">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6 }}
        className="text-3xl font-bold mb-16 text-center"
      >
        {t("contact.title")}
      </motion.h2>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
          transition={{ duration: 0.6 }}
          className="p-8 rounded-lg bg-secondary/20 backdrop-blur-sm border border-accent/20"
        >
          <h3 className="text-2xl font-bold mb-8">
            {t("contact.letsconnect")}
          </h3>

          <div className="space-y-6 mb-10">
            {contactInfo.map((item, index) => (
              <div key={index} className="flex items-center">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center mr-4">
                  {item.icon}
                </div>
                <span>{item.text}</span>
              </div>
            ))}
          </div>

          <h4 className="text-lg font-semibold mb-4">
            {t("contact.followme")}
          </h4>
          <div className="flex space-x-4 mb-6">
            {socialLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center transition-colors hover:bg-accent/30"
                aria-label={link.label}
              >
                {link.icon}
              </a>
            ))}
          </div>

          <a
            href="/resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-accent hover:text-accent-light transition-colors"
          >
            <FileIcon className="h-5 w-5" />
            <span>{t("contact.downloadresume")}</span>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
          transition={{ duration: 0.6 }}
          className="p-8 rounded-lg bg-secondary/20 backdrop-blur-sm border border-accent/20"
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("contact.name")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("contact.namePlaceholder")}
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("contact.email")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("contact.emailPlaceholder")}
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("contact.message")}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("contact.messagePlaceholder")}
                        className="min-h-32"
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || !recaptchaLoaded}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  t("contact.send")
                )}
              </Button>

              {statusMessage && (
                <div
                  className={`mt-4 p-3 rounded ${
                    statusMessage === t("contact.success")
                      ? "bg-green-500/20 text-green-200"
                      : "bg-red-500/20 text-red-200"
                  }`}
                >
                  {statusMessage}
                </div>
              )}

              <div className="text-xs text-muted-foreground mt-4 text-center">
                {t("recaptcha.protected")}
                <a
                  href="https://policies.google.com/privacy"
                  className="text-accent hover:underline mx-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("recaptcha.privacy")}
                </a>
                {t("root.and")}
                <a
                  href="https://policies.google.com/terms"
                  className="text-accent hover:underline mx-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("recaptcha.terms")}
                </a>
                {t("root.apply")}
              </div>
            </form>
          </Form>
        </motion.div>
      </div>
    </section>
  );
};
